/**
 * main.ts — Application entry point.
 *
 * Owns the two things that only exist once: the **level picker** and the
 * currently running game. The flow is:
 *
 *   menu ──pick a level──> Game + Hud + TouchControls
 *     ▲                        │
 *     └──── ☰ Levels ──────────┘   (tap restarts the same level instead)
 *
 * Keep this file thin — all gameplay lives in core/, entities/, and systems/,
 * and everything the menu draws lives in ui/menu.ts. What's genuinely here is
 * the wiring: which level is loaded, when a game is torn down, and when a win
 * gets recorded.
 */
import { Game } from './core/Game';
import { Hud } from './ui/hud';
import { Menu } from './ui/menu';
import { TouchControls } from './ui/touchControls';
import { markCompleted } from './ui/progress';
import { mutedSetting } from './ui/settings';
import { CloudEvents, CloudSave } from './cloud/session';
import { firebaseConnector } from './cloud/firebase';
import { activeLevel, setActiveLevel } from './config/level';
import { generateLevel } from './systems/levelgen';
import { CAMPAIGN_LENGTH, bandLabel, campaignLevel } from './systems/levelgen/campaign';
import { GameEvents } from './types';
import { DIFFICULTIES, type Difficulty, type LevelSpec } from './systems/levelgen/types';

const parent = document.getElementById('game');
if (!parent) throw new Error('#game mount point missing from index.html');

/**
 * Optional, and silent when it isn't configured: `firebaseConnector()` returns
 * null for a build with no Firebase env vars, and the menu then draws no account
 * row. Nothing below this line is allowed to depend on it working.
 */
const cloud = new CloudSave(firebaseConnector());

const menu = new Menu(parent, { cloud });

let game: Game | undefined;
let hud: Hud | undefined;
let touch: TouchControls | undefined;

// A sync can land the account's mute setting while a game is already running —
// most obviously on a `?level=N` deep link, which starts a run before the first
// sync finishes. The equality check is what stops this from bouncing back and
// forth against the push that `onMuteChange` triggers.
cloud.events.on(CloudEvents.CHANGED, () => {
  const muted = mutedSetting();
  if (game && game.sound.isMuted !== muted) {
    game.sound.setMuted(muted);
    hud?.refreshSound();
  }
});

/** What's loaded: a campaign level (so a win counts), or a one-off from a URL. */
let current: { spec: LevelSpec; label: string; number?: number } | undefined;

/**
 * A level named directly in the URL, if any.
 *
 * Three forms, in order of precedence:
 *
 *   `?level=4`                     the campaign's level 4
 *   `?seed=x&difficulty=hard`      a one-off generated town
 *   (nothing)                      no direct level — show the menu
 *
 * The seed form deliberately does **not** count toward progress: it isn't one
 * of the campaign's thousand, so ticking a box for it would be meaningless.
 */
function levelFromUrl(): typeof current {
  const params = new URLSearchParams(window.location.search);

  const asked = Number(params.get('level'));
  if (Number.isInteger(asked) && asked >= 1 && asked <= CAMPAIGN_LENGTH) {
    return campaignEntry(asked);
  }

  const seed = params.get('seed');
  const band = params.get('difficulty');
  if (!seed && !band) return undefined;

  const difficulty: Difficulty = DIFFICULTIES.includes(band as Difficulty)
    ? (band as Difficulty)
    : 'medium';
  const spec = generateLevel({ seed: seed ?? `${Date.now().toString(36)}`, difficulty });
  return { spec, label: `Seed “${spec.seed}” · ${bandLabel(spec)}` };
}

function campaignEntry(number: number): NonNullable<typeof current> {
  const spec = campaignLevel(number);
  // `bandLabel`, so the guided walk is named "Tutorial" here exactly as it is
  // on the button that started it.
  return { spec, label: `Level ${number} · ${bandLabel(spec)}`, number };
}

/** Tear down whatever is running. Safe to call when nothing is. */
function teardown(): void {
  touch?.dispose();
  hud?.dispose();
  game?.dispose();
  touch = hud = game = undefined;
}

/**
 * Build a fresh game on the level in `current`.
 *
 * The level is swapped in **before** the `Game` is constructed, because the
 * town is built in that constructor — see `config/level.ts`.
 */
function launch(): void {
  if (!current) return;
  setActiveLevel(current.spec);

  let nextLevelDifficulty: string | undefined;
  if (current.number !== undefined) {
    const nextNum = current.number < CAMPAIGN_LENGTH ? current.number + 1 : 1;
    const nextEntry = campaignEntry(nextNum);
    nextLevelDifficulty = bandLabel(nextEntry.spec);
  }

  game = new Game(parent!);
  hud = new Hud(parent!, game.events, {
    levelLabel: current.label,
    onMenu: openMenu,
    onNextLevel: current.number !== undefined ? nextLevel : undefined,
    nextLevelDifficulty,
    soundManager: game.sound,
  });
  // The on-screen stick. Always mounted: it only draws while a pointer is
  // dragging, so it costs a mouse user nothing.
  touch = new TouchControls(parent!, game.input);

  game.onRestart = restart;
  hud.onRestart = restart; // tapping the end-of-run overlay
  // Every route to a mute toggle ends up here, so cloud save only needs the one
  // hook rather than one per button and keypress.
  game.sound.onMuteChange = () => cloud.push();
  // A finished walk is what earns the tick in the picker. Only campaign levels
  // have a number, so a URL-seeded town wins nothing.
  const finished = current.number;
  if (finished !== undefined) {
    game.events.on(GameEvents.WON, () => {
      markCompleted(finished);
      cloud.push(); // no-op when signed out, beyond stamping the local clock
    });
  }

  game.emitInitialState();
  game.start();

  // Dev-only: expose the running game for debugging / automated browser tests.
  // Stripped from production builds by the `import.meta.env.DEV` guard.
  if (import.meta.env.DEV) {
    const w = window as unknown as { __DOGWALKER__: Game; __LEVEL__: unknown };
    w.__DOGWALKER__ = game;
    // The level being played, so the smoke-run and the console can see which
    // town this is and where its objectives are without guessing.
    w.__LEVEL__ = activeLevel();
  }
}

/** Advance to the next campaign level. */
function nextLevel(): void {
  if (current?.number === undefined) return;
  const nextNum = current.number < CAMPAIGN_LENGTH ? current.number + 1 : 1;
  current = campaignEntry(nextNum);
  teardown();
  launch();
}

/** Same level, fresh run. */
function restart(): void {
  teardown();
  launch();
}

/** Abandon the run and go back to the picker. */
function openMenu(): void {
  teardown();
  menu.open();
}

menu.onPick = (number) => {
  current = campaignEntry(number);
  menu.close();
  teardown(); // nothing should be running, but a stray game would keep ticking
  launch();
};

// A URL that names a level goes straight into it; otherwise the picker opens.
current = levelFromUrl();
if (current) launch();
else menu.open();
